import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

// ==================== EMPLOYER NAME PRIVACY ====================
// Global sanitizer that strips employer personal names from ALL Supabase
// query results. This is a safety net — even if a query fetches .name,
// it gets replaced with business_name before any component sees it.

function sanitizeEmployerNames(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    obj.forEach(sanitizeEmployerNames);
    return obj;
  }
  // If this object has role=employer/owner and a name field, replace it
  if ((obj.role === 'employer' || obj.role === 'owner') && 'name' in obj) {
    obj.name = obj.business_name || 'Employer';
  }
  // Handle nested employer/owner objects from Supabase joins
  // (these may not have a 'role' field but are always employer profiles)
  for (const key of ['employer', 'owner']) {
    if (obj[key] && typeof obj[key] === 'object' && 'name' in obj[key]) {
      obj[key].name = obj[key].business_name || 'Employer';
    }
  }
  // Recurse into all values to catch deeply nested structures
  for (const val of Object.values(obj)) {
    if (val && typeof val === 'object') sanitizeEmployerNames(val);
  }
  return obj;
}

const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Custom fetch wrapper that intercepts Supabase REST API responses
// and auto-sanitizes employer names before they reach any component
const originalFetch = window.fetch;

window.fetch = async function (...args) {
  const response = await originalFetch.apply(this, args);
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

  // Only intercept Supabase REST API calls (not auth, storage, etc.)
  if (url && supabaseUrl && url.startsWith(supabaseUrl) && url.includes('/rest/')) {
    // Skip sanitization on admin pages — admins need real employer names
    const path = window.location.pathname;
    if (path.startsWith('/admin')) {
      return response;
    }
    // Clone the response so we can read and modify the body
    const clone = response.clone();
    try {
      const data = await clone.json();
      sanitizeEmployerNames(data);
      return new Response(JSON.stringify(data), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    } catch {
      // Not JSON or parse failed — return original
      return response;
    }
  }
  return response;
};

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Check if running in PWA standalone mode
let isStandalone = false;
try {
  isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                 window.navigator.standalone === true;
} catch (e) {
  // Ignore errors in restricted environments
}

// Safe storage wrapper for the auth session.
//
// WHY THIS EXISTS: in-app browsers (Facebook/Messenger, Instagram), private
// mode, and phones with strict "block storage" privacy settings throw when the
// app touches localStorage. The original fix fell back to an in-memory object,
// which stopped the crash but was VOLATILE — it was wiped by any full-page
// reload. Because login does `window.location.href = '/dashboard'` (a full
// reload), the session was destroyed on exactly those devices, so the user was
// bounced back to the landing page still logged out. That was the recurring
// "PESO can log in on a computer but not on the phone" bug.
//
// FIX: when localStorage is unavailable we now persist the session to a COOKIE
// (which survives reloads — the same durable-store strategy @supabase/ssr uses)
// plus an in-memory mirror for fast same-page reads. Normal browsers where
// localStorage works are 100% unaffected: the probe below passes, so they only
// ever use localStorage and no cookie is written.

// Cookie-backed store used ONLY when localStorage is blocked. Supabase session
// tokens can exceed the ~4KB single-cookie limit, so values are split across
// numbered chunk cookies (key.0, key.1, ...) and reassembled on read.
const COOKIE_CHUNK_SIZE = 3200; // leaves room for the cookie name + attributes
const cookieSecure = (() => {
  try { return window.location.protocol === 'https:'; } catch (e) { return true; }
})();
// `Secure` on HTTPS (production); omitted on http://localhost so local/dev and
// the preview panel can store the cookie. 90-day lifetime; refresh tokens rotate.
const COOKIE_ATTRS = `path=/;max-age=7776000;SameSite=Lax${cookieSecure ? ';Secure' : ''}`;

const readRawCookie = (name) => {
  const prefix = name + '=';
  const parts = (document.cookie || '').split('; ');
  for (const part of parts) {
    if (part.indexOf(prefix) === 0) return part.slice(prefix.length);
  }
  return null;
};

const cookieStorage = {
  getItem: (key) => {
    let encoded = '';
    let i = 0;
    for (;;) {
      const chunk = readRawCookie(`${key}.${i}`);
      if (chunk === null) break;
      encoded += chunk;
      i++;
    }
    if (i === 0) return null;
    try {
      return decodeURIComponent(encoded);
    } catch (e) {
      return null; // corrupt/partial cookie — fail safe to logged-out
    }
  },
  setItem: (key, value) => {
    cookieStorage.removeItem(key); // clear any previous (possibly longer) chunk set
    const encoded = encodeURIComponent(value); // always ASCII, safe to slice
    const chunkCount = Math.max(1, Math.ceil(encoded.length / COOKIE_CHUNK_SIZE));
    for (let i = 0; i < chunkCount; i++) {
      const slice = encoded.slice(i * COOKIE_CHUNK_SIZE, (i + 1) * COOKIE_CHUNK_SIZE);
      document.cookie = `${key}.${i}=${slice};${COOKIE_ATTRS}`;
    }
  },
  removeItem: (key) => {
    let i = 0;
    while (readRawCookie(`${key}.${i}`) !== null) {
      document.cookie = `${key}.${i}=;path=/;max-age=0;SameSite=Lax${cookieSecure ? ';Secure' : ''}`;
      i++;
    }
  }
};

// Probe localStorage once. On normal browsers this passes and we use ONLY
// localStorage (unchanged behavior). On blocked-storage devices it fails and we
// use the durable cookie store plus an in-memory mirror.
let localStorageUsable = null;
const canUseLocalStorage = () => {
  if (localStorageUsable !== null) return localStorageUsable;
  try {
    const probeKey = '__bt_ls_probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    localStorageUsable = true;
  } catch (e) {
    localStorageUsable = false;
  }
  return localStorageUsable;
};

const memoryStorage = {};
const safeStorage = {
  getItem: (key) => {
    if (canUseLocalStorage()) {
      try { return localStorage.getItem(key); } catch (e) { /* fall through */ }
    }
    const fromCookie = cookieStorage.getItem(key);
    if (fromCookie !== null) return fromCookie;
    return key in memoryStorage ? memoryStorage[key] : null;
  },
  setItem: (key, value) => {
    if (canUseLocalStorage()) {
      try { localStorage.setItem(key, value); return; } catch (e) { /* fall through */ }
    }
    memoryStorage[key] = value;       // fast, consistent same-page reads
    try { cookieStorage.setItem(key, value); } catch (e) { /* cookies also blocked */ }
  },
  removeItem: (key) => {
    if (canUseLocalStorage()) {
      try { localStorage.removeItem(key); return; } catch (e) { /* fall through */ }
    }
    delete memoryStorage[key];
    try { cookieStorage.removeItem(key); } catch (e) { /* ignore */ }
  }
};

// Create Supabase client with improved session handling for PWA
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Use safe storage wrapper (handles blocked localStorage in in-app browsers)
    storage: safeStorage,
    // Shorter flow timeout for PWA to prevent infinite waits
    flowType: 'pkce',
  }
});

// Helper to detect PWA mode
export const isPWAMode = () => isStandalone;

/**
 * Get safe display name for any profile. ALWAYS use this for displaying names.
 * For employers/owners: returns business_name (never personal name).
 * For job seekers/contractors: returns personal name.
 */
export const getDisplayName = (profile) => {
  if (!profile) return 'Unknown User';
  if (profile.role === 'employer' || profile.role === 'owner')
    return profile.business_name || 'Employer';
  return profile.name || 'User';
};

// Helper to wrap any async operation with a timeout
export const withTimeout = async (promise, timeoutMs = 10000, errorMessage = 'Operation timed out') => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
};

// Helper to sign in with timeout (prevents mobile hangs)
export const signInWithTimeout = async (email, password, timeoutMs = 15000) => {
  try {
    const result = await withTimeout(
      supabase.auth.signInWithPassword({ email, password }),
      timeoutMs,
      'Login timed out. Please check your internet connection and try again.'
    );
    return result;
  } catch (error) {
    if (error.message.includes('timed out')) {
      return { data: null, error: { message: error.message } };
    }
    throw error;
  }
};

// Helper to get session with timeout and retry for PWA
export const getSessionWithTimeout = async (timeoutMs = 5000) => {
  return new Promise(async (resolve) => {
    // Set timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.warn('[Supabase] Session retrieval timed out after', timeoutMs, 'ms');
      resolve({ session: null, error: 'timeout' });
    }, timeoutMs);

    try {
      const { data, error } = await supabase.auth.getSession();
      clearTimeout(timeout);
      
      if (error) {
        console.error('[Supabase] getSession error:', error);
        // Try to refresh if there's an error
        try {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshData?.session) {
            console.log('[Supabase] Session recovered via refresh');
            resolve({ session: refreshData.session, error: null });
            return;
          }
        } catch (refreshErr) {
          console.error('[Supabase] Refresh attempt failed:', refreshErr);
        }
        resolve({ session: null, error });
        return;
      }

      // If in PWA mode and no session, try refresh once more
      if (isStandalone && !data?.session) {
        console.log('[Supabase] PWA mode detected with no session, attempting refresh...');
        try {
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (refreshData?.session) {
            console.log('[Supabase] PWA session recovered via refresh');
            resolve({ session: refreshData.session, error: null });
            return;
          }
        } catch (refreshErr) {
          console.log('[Supabase] PWA refresh failed, user needs to login');
        }
      }

      resolve({ session: data?.session || null, error: null });
    } catch (err) {
      clearTimeout(timeout);
      console.error('[Supabase] Unexpected error getting session:', err);
      resolve({ session: null, error: err });
    }
  });
};
